/**
 * Chat conversation workspace scope helpers (draft vs persisted, apply-to-app toast).
 */

import { toast } from 'sonner'
import { workspaceForFilePath } from '@/lib/workspace-path-match'

type WorkspaceLike = { id: string; contentRoot?: string | null }

/** Composer chip ids from thread tags when `workspace_scope` is null. */
export function conversationScopeIdsFromThreadTags(
  taggedOrganizationIds?: string[] | null,
): string[] | null {
  if (!Array.isArray(taggedOrganizationIds) || taggedOrganizationIds.length === 0) {
    return null
  }
  const ids = taggedOrganizationIds.map((id) => String(id).trim()).filter(Boolean)
  return ids.length > 0 ? ids : null
}

/**
 * True when the agent panel should drop a hosted thread because it does not
 * belong in the current sidebar workspace filter.
 * Unknown meta / empty active set (all workspaces) keep the thread.
 */
export function shouldReleaseAgentPanelForWorkspaceFilter(
  thread:
    | {
        workspaceId?: string | null
        organizationId?: string | null
        taggedOrganizationIds?: string[] | null
      }
    | null
    | undefined,
  activeWorkspaceIds: string[],
): boolean {
  if (!thread) return false
  if (activeWorkspaceIds.length === 0) return false
  const tags = thread.taggedOrganizationIds ?? []
  const orgFallback = thread.organizationId ? [thread.organizationId] : []
  const effectiveTags = tags.length > 0 ? tags : orgFallback
  if (effectiveTags.length > 0) {
    return !effectiveTags.some((id) => activeWorkspaceIds.includes(id))
  }
  const workspaceId = thread.workspaceId ?? 'personal'
  if (workspaceId === 'personal') return false
  return !activeWorkspaceIds.includes(workspaceId)
}

/**
 * Unbind a hosted agent-panel chat only after the sidebar workspace filter
 * moves. Opening a row changes the thread id, not the filter. Unbinding on
 * that pass drops chats the list already showed (mobile chats are filed on
 * organization_id with empty tags) and the in-flight load never clears
 * "Loading conversation...".
 */
export function shouldUnbindAgentPanelForWorkspacePass(input: {
  filterChanged: boolean
  threadId: string | null | undefined
  activeWorkspaceCount: number
}): boolean {
  if (!input.threadId || input.threadId.startsWith('new-')) return false
  if (input.activeWorkspaceCount === 0) return false
  return input.filterChanged
}

/**
 * Last-thread restore may proceed when the sidebar is unfiltered, or when cached
 * tags overlap the active set. Unknown meta under a narrowed sidebar is blocked
 * (restoring first used to put Hop chats back after a Kootenay-only switch).
 */
export function canRestoreAgentPanelThread(
  meta:
    | {
        workspaceId?: string | null
        organizationId?: string | null
        taggedOrganizationIds?: string[] | null
      }
    | null
    | undefined,
  activeWorkspaceIds?: string[] | null,
): boolean {
  if (!activeWorkspaceIds || activeWorkspaceIds.length === 0) return true
  // Narrowed sidebar: do not restore a last-thread until tags are known.
  // Unknown-meta used to return true and put Hop chats back after a Kootenay-only switch.
  if (!meta) return false
  return !shouldReleaseAgentPanelForWorkspaceFilter(meta, activeWorkspaceIds)
}

function normalizeContentRootForMatch(root: string): string {
  return root.replace(/\\/g, '/').replace(/^\/+|\/+$/g, '')
}

/**
 * Content-root paths to persist on ai_conversations.workspace_scope.
 * - undefined: omit from request (no change)
 * - null: clear saved scope (DB NULL = follow all active workspaces)
 * - string[]: narrowed scope for this conversation
 *
 * Tagging note: even when this returns null (follow all), the chat send path must
 * still send live `workspaceScopeForTags` so thread tags union the full active set
 * (see collectOrgIdsForChatTurn). Do not use this helper alone for sidebar tags.
 */
export function workspaceScopePathsForPersist(
  draftScopeIds: string[] | null,
  activeWorkspaceIds: string[],
  workspaces: WorkspaceLike[],
): string[] | null | undefined {
  if (draftScopeIds === null) {
    return null
  }

  const isSameAsActive =
    draftScopeIds.length === activeWorkspaceIds.length &&
    activeWorkspaceIds.every((id) => draftScopeIds.includes(id))

  if (isSameAsActive) {
    return null
  }

  const paths = draftScopeIds
    .map((id) => workspaces.find((w) => w.id === id)?.contentRoot)
    .filter((p): p is string => Boolean(p?.trim()))

  return paths.length > 0 ? paths : null
}

/**
 * Org that should own a pasted chat image.
 * One selected workspace wins. With several selected, prefer the open document's
 * workspace when it is in the set. If the sidebar scope has not loaded, use the
 * open document's workspace. Never omit this when a workspace can be resolved -
 * upload-chat rejects a missing id rather than filing under the account primary org.
 */
export function organizationIdForChatImageUpload(opts: {
  resolvedScopeIds: string[]
  openDocumentPath?: string | null
  workspaces: Array<{ id: string; contentRoot?: string | null; name?: string; slug?: string }>
}): string | null {
  const ids = opts.resolvedScopeIds.map((id) => String(id).trim()).filter(Boolean)
  const openWs = workspaceForFilePath(
    opts.openDocumentPath || undefined,
    opts.workspaces as Parameters<typeof workspaceForFilePath>[1],
  )
  if (ids.length === 0) return openWs?.id ?? null
  if (ids.length === 1) return ids[0]
  if (openWs && ids.includes(openWs.id)) return openWs.id
  return ids[0]
}

export function contentRootsToScopeIds(
  scopePaths: string[] | null | undefined,
  workspaces: WorkspaceLike[],
): string[] | null {
  if (!scopePaths?.length) return null
  const normalizedPaths = new Set(scopePaths.map(normalizeContentRootForMatch))
  const ids = workspaces
    .filter(
      (w) => w.contentRoot && normalizedPaths.has(normalizeContentRootForMatch(w.contentRoot)),
    )
    .map((w) => w.id)
  return ids.length > 0 ? ids : null
}

/**
 * Draft composer scope for a loaded chat: saved content-root paths, else thread tags.
 * Tags do not need the workspace list (ids are already org UUIDs).
 */
export function hydrateConversationDraftScope(opts: {
  scopePaths?: string[] | null
  taggedOrganizationIds?: string[] | null
  workspaces: WorkspaceLike[]
}): string[] | null {
  const fromPaths = contentRootsToScopeIds(opts.scopePaths, opts.workspaces)
  if (fromPaths && fromPaths.length > 0) return fromPaths
  return conversationScopeIdsFromThreadTags(opts.taggedOrganizationIds)
}

/**
 * Open-doc snap is only for untagged follow-sidebar drafts.
 * Saved / tagged chats must keep every workspace (do not collapse to the open file).
 * `undefined` = leave conversationScope unchanged.
 */
export function composerScopeAfterOpenDocument(opts: {
  userSetScope: boolean
  conversationScope: string[] | null
  openDocumentWorkspaceId: string | null | undefined
  /** When set, do not snap the draft onto a workspace the sidebar has hidden. */
  activeWorkspaceIds?: string[]
}): string[] | undefined {
  if (opts.userSetScope) return undefined
  if (opts.conversationScope && opts.conversationScope.length > 0) return undefined
  if (
    opts.openDocumentWorkspaceId &&
    opts.activeWorkspaceIds &&
    opts.activeWorkspaceIds.length > 0 &&
    !opts.activeWorkspaceIds.includes(opts.openDocumentWorkspaceId)
  ) {
    return undefined
  }
  if (opts.openDocumentWorkspaceId) return [opts.openDocumentWorkspaceId]
  return undefined
}

/** True when explicit draft scope differs from the global sidebar active set. */
export function draftScopeDiffersFromActive(
  draftScopeIds: string[] | null,
  activeWorkspaceIds: string[],
): boolean {
  if (draftScopeIds === null) return false
  if (draftScopeIds.length !== activeWorkspaceIds.length) return true
  const active = new Set(activeWorkspaceIds)
  return draftScopeIds.some((id) => !active.has(id))
}

const APPLY_SCOPE_TOAST_MS = 10_000

/**
 * After first message in a chat, offer to apply conversation scope to the whole app (sidebar).
 */
export function showApplyScopeToAppToast(
  scopeIds: string[],
  setActiveWorkspaceIds: (ids: string[]) => void,
  previousActiveIds: string[],
): void {
  const snapshot = [...previousActiveIds]

  toast.message('Use this scope for the whole app?', {
    description: 'Apply the workspaces used in this chat to Browse, Flows, and Inbox.',
    duration: APPLY_SCOPE_TOAST_MS,
    action: {
      label: 'Apply',
      onClick: () => {
        setActiveWorkspaceIds([...scopeIds])
        toast.success('Sidebar scope updated', {
          duration: APPLY_SCOPE_TOAST_MS,
          action: {
            label: 'Undo',
            onClick: () => setActiveWorkspaceIds(snapshot),
          },
        })
      },
    },
  })
}
